import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentUser } from '@/hooks/useUserManagement';
import {
  LayoutDashboard,
  Building,
  Users,
  Target,
  CalendarClock,
  MessageSquare,
  Gift,
  BarChart3,
  Settings,
  FileSpreadsheet,
  LogIn,
  LogOut,
  TrendingUp,
  Calendar,
  Network,
  Map,
  Download,
  Database,
  FolderOpen,
  UserCog,
  FileText,
  Plug,
  BookOpen,
  Bot,
  MessageSquarePlus,
  DollarSign,
  Shield,
  Workflow,
  Cloud,
} from 'lucide-react';

interface SidebarProps {
  collapsed?: boolean;
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user, signOut } = useAuth();
  const { data: currentUser } = useCurrentUser();

  if (isMobile) return null;

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className={cn(
        'flex h-full flex-col border-r bg-background px-2 pt-6 pb-4',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex flex-col justify-between h-full">
        <div>
          <div className={cn('mb-8 flex items-center', collapsed ? 'justify-center' : 'px-2')}>
            {collapsed ? (
              <div className="h-8 w-8 rounded-full bg-primary" aria-hidden="true" />
            ) : (
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary" aria-hidden="true" />
                <h2 className="text-lg font-medium">Spherical SRM</h2>
              </div>
            )}
          </div>

          <div className="space-y-1" role="list">
            <NavItem
              to="/dashboard"
              icon={<LayoutDashboard className="h-5 w-5" />}
              label="Dashboard"
              collapsed={collapsed}
              active={location.pathname === '/dashboard'}
            />
            <NavItem
              to="/organizations"
              icon={<Building className="h-5 w-5" />}
              label="Organizations"
              collapsed={collapsed}
              active={location.pathname.startsWith('/organizations')}
            />
            <NavItem
              to="/organization-hierarchy"
              icon={<Network className="h-5 w-5" />}
              label="Org Hierarchy"
              collapsed={collapsed}
              active={location.pathname.startsWith('/organization-hierarchy')}
            />
            <NavItem
              to="/contacts"
              icon={<Users className="h-5 w-5" />}
              label="Contacts"
              collapsed={collapsed}
              active={location.pathname.startsWith('/contacts')}
            />
            <NavItem
              to="/opportunities"
              icon={<Target className="h-5 w-5" />}
              label="Opportunities"
              collapsed={collapsed}
              active={location.pathname.startsWith('/opportunities')}
            />
            <NavItem
              to="/projects"
              icon={<FolderOpen className="h-5 w-5" />}
              label="Projects"
              collapsed={collapsed}
              active={location.pathname.startsWith('/projects')}
            />
            <NavItem
              to="/invoices"
              icon={<FileText className="h-5 w-5" />}
              label="Invoices"
              collapsed={collapsed}
              active={location.pathname.startsWith('/invoices')}
            />
            <NavItem
              to="/tasks"
              icon={<CalendarClock className="h-5 w-5" />}
              label="Tasks"
              collapsed={collapsed}
              active={location.pathname.startsWith('/tasks')}
            />
            <NavItem
              to="/calendar"
              icon={<Calendar className="h-5 w-5" />}
              label="Calendar"
              collapsed={collapsed}
              active={location.pathname.startsWith('/calendar')}
            />
            <NavItem
              to="/activities"
              icon={<MessageSquare className="h-5 w-5" />}
              label="Activities"
              collapsed={collapsed}
              active={
                location.pathname.startsWith('/activities') && !location.pathname.includes('/types')
              }
            />
            <NavItem
              to="/activities/types"
              icon={<Settings className="h-5 w-5" />}
              label="Activity Types"
              collapsed={collapsed}
              active={location.pathname === '/activities/types'}
            />
            <NavItem
              to="/communications"
              icon={<MessageSquare className="h-5 w-5" />}
              label="Communications"
              collapsed={collapsed}
              active={
                location.pathname.startsWith('/communications') &&
                !location.pathname.includes('/hub')
              }
            />
            <NavItem
              to="/communications/hub"
              icon={<MessageSquarePlus className="h-5 w-5" />}
              label="Comm Hub"
              collapsed={collapsed}
              active={location.pathname === '/communications/hub'}
            />
            <NavItem
              to="/gifts"
              icon={<Gift className="h-5 w-5" />}
              label="Gifts"
              collapsed={collapsed}
              active={location.pathname.startsWith('/gifts')}
            />
            <NavItem
              to="/landexpand"
              icon={<TrendingUp className="h-5 w-5" />}
              label="Land & Expand"
              collapsed={collapsed}
              active={
                location.pathname.startsWith('/landexpand') ||
                location.pathname.startsWith('/land-expand')
              }
            />
            <NavItem
              to="/import"
              icon={<FileSpreadsheet className="h-5 w-5" />}
              label="Import Data"
              collapsed={collapsed}
              active={location.pathname.startsWith('/import')}
            />
            <NavItem
              to="/export"
              icon={<Database className="h-5 w-5" />}
              label="Export Data"
              collapsed={collapsed}
              active={location.pathname.startsWith('/export')}
            />
            <NavItem
              to="/integrations/hubspot"
              icon={<Plug className="h-5 w-5" />}
              label="HubSpot"
              collapsed={collapsed}
              active={location.pathname.startsWith('/integrations/hubspot')}
            />
            <NavItem
              to="/automation"
              icon={<Bot className="h-5 w-5" />}
              label="Automation"
              collapsed={collapsed}
              active={location.pathname.startsWith('/automation')}
            />
            <NavItem
              to="/analytics"
              icon={<BarChart3 className="h-5 w-5" />}
              label="Analytics"
              collapsed={collapsed}
              active={
                location.pathname.startsWith('/analytics') &&
                !location.pathname.includes('/financial')
              }
            />
            <NavItem
              to="/analytics/financial"
              icon={<DollarSign className="h-5 w-5" />}
              label="Financial Analytics"
              collapsed={collapsed}
              active={location.pathname === '/analytics/financial'}
            />
            <NavItem
              to="/docs"
              icon={<BookOpen className="h-5 w-5" />}
              label="User Guide"
              collapsed={collapsed}
              active={location.pathname.startsWith('/docs')}
            />
          </div>
        </div>
        <div className="space-y-1" role="list">
          {/* Admin-only section */}
          {currentUser?.role === 'admin' && (
            <>
              <NavItem
                to="/admin/users"
                icon={<UserCog className="h-5 w-5" />}
                label="User Management"
                collapsed={collapsed}
                active={location.pathname.startsWith('/admin/users')}
              />
              <NavItem
                to="/admin/security"
                icon={<Shield className="h-5 w-5" />}
                label="Security"
                collapsed={collapsed}
                active={location.pathname === '/admin/security'}
              />
            </>
          )}
          <NavItem
            to="/settings"
            icon={<Settings className="h-5 w-5" />}
            label="Settings"
            collapsed={collapsed}
            active={location.pathname === '/settings'}
          />
          <NavItem
            to="/integrations"
            icon={<Plug className="h-5 w-5" />}
            label="Integrations"
            collapsed={collapsed}
            active={location.pathname === '/integrations'}
          />
          {user ? (
            <NavItem
              to="#"
              icon={<LogOut className="h-5 w-5" />}
              label="Sign Out"
              collapsed={collapsed}
              onClick={async () => await signOut()}
            />
          ) : (
            <NavItem
              to="/auth"
              icon={<LogIn className="h-5 w-5" />}
              label="Sign In"
              collapsed={collapsed}
              active={location.pathname.startsWith('/auth')}
            />
          )}
        </div>
      </div>
    </nav>
  );
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
  active?: boolean;
  onClick?: () => void;
}

function NavItem({ to, icon, label, collapsed = false, active = false, onClick }: NavItemProps) {
  const content = (
    <>
      <span aria-hidden="true">{icon}</span>
      {!collapsed && <span className="ml-2 truncate">{label}</span>}
    </>
  );

  // If there's a custom onClick handler (like sign out), handle it differently
  if (onClick) {
    return (
      <Button
        role="listitem"
        variant={active ? 'default' : 'ghost'}
        size="sm"
        className={cn(
          'w-full justify-start h-9 mb-1',
          collapsed && 'justify-center',
          active ? (collapsed ? 'px-2' : 'px-3') : collapsed ? 'px-2' : 'px-3 text-muted-foreground'
        )}
        onClick={onClick}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
      >
        <div className="flex items-center w-full">{content}</div>
      </Button>
    );
  }

  // For regular navigation, use asChild to properly handle the Link component
  return (
    <Button
      role="listitem"
      variant={active ? 'default' : 'ghost'}
      size="sm"
      className={cn(
        'w-full justify-start h-9 mb-1 select-none transition-colors',
        collapsed && 'justify-center',
        active ? (collapsed ? 'px-2' : 'px-3') : collapsed ? 'px-2' : 'px-3 text-muted-foreground'
      )}
      asChild
      aria-current={active ? 'page' : undefined}
    >
      <Link to={to} className="flex items-center w-full" aria-label={label}>
        {content}
      </Link>
    </Button>
  );
}
